import React from "react";
import styled from "styled-components";
import { colors } from "../../styles/style";

/**
 * How often the store will charge for a package.
 *
 * Derived from the store's own packageType, never assumed. The paywall used to
 * say "per month" about whichever plan came back first — true only by accident
 * while monthly was the only plan on sale.
 */
export const billingPeriodLabel = (pkg: any): string => {
  switch (pkg?.packageType) {
    case "ANNUAL":
      return "per year";
    case "MONTHLY":
      return "per month";
    case "WEEKLY":
      return "per week";
    case "SIX_MONTH":
      return "every six months";
    case "THREE_MONTH":
      return "every three months";
    case "TWO_MONTH":
      return "every two months";
    case "LIFETIME":
      return "once";
    default:
      /* An unfamiliar plan is described without a period rather than with a
         wrong one. The price itself is still the store's own string. */
      return "";
  }
};

interface PlanChoicesProps {
  packages: any[];
  selectedProductId?: string;
  onSelect: (pkg: any) => void;
}

/**
 * Lets someone pick which plan to buy.
 *
 * Prices are the store's own localised strings, rendered verbatim: apps get
 * rejected when the in-app price and the purchase sheet disagree, so nothing
 * here is reformatted, converted or derived.
 */
export const PlanChoices: React.FC<PlanChoicesProps> = ({
  packages,
  selectedProductId,
  onSelect,
}) => (
  <Row>
    {packages.map((pkg) => {
      const selected = pkg.productId === selectedProductId;
      return (
        <Choice
          key={pkg.productId}
          type="button"
          $selected={selected}
          aria-pressed={selected}
          onClick={() => onSelect(pkg)}
        >
          <Price>{pkg.displayPrice}</Price>
          <Period>{billingPeriodLabel(pkg)}</Period>
        </Choice>
      );
    })}
  </Row>
);

const Row = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  padding: 0 16px;
  margin: 12px 0 0 0;
`;

const Choice = styled.button<{ $selected: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 12px 8px;
  cursor: pointer;
  border-radius: 20px;
  border: 1px solid
    ${(props) => (props.$selected ? colors.warmthBrown : colors.borderWhite)};
  background-color: ${(props) =>
    props.$selected ? colors.softPink : colors.white};
`;

const Price = styled.span`
  color: ${colors.darkBrown};
  font-size: 16px;
  font-weight: 500;
`;

const Period = styled.span`
  color: ${colors.warmthBrown};
  font-size: 13px;
`;
